import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FieldValues, useFormContext } from 'react-hook-form';
import { isIndonesiaCountry } from '@anytag/shared-utils';
import {
  RegionField,
  ProvinceField,
  RegionFieldProps,
  ProvinceFieldProps,
  SingleRegionFieldProps,
  SingleProvinceFieldProps,
  MultipleRegionFieldProps,
  MultipleProvinceFieldProps
} from '@anytag/shared-molecules';

export interface RegionAndProvinceFieldProps<T extends string = string> {
  countryId: string;
  regionFieldProps: Omit<RegionFieldProps<T>, 'countryId' | 'provinceId' | 'skipQuery'>;
  provinceFieldProps: Omit<ProvinceFieldProps<T>, 'countryId'>;
}

export const RegionAndProvinceField = <T extends string>({
  countryId,
  regionFieldProps,
  provinceFieldProps
}: RegionAndProvinceFieldProps<T>) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<FieldValues>();

  const provinceValue = watch(provinceFieldProps.name);
  const isMultipleProvinces = provinceFieldProps.multiple && Array.isArray(provinceValue);
  const commonProvinceFieldProps: Pick<ProvinceFieldProps<T>, 'countryId' | 'help' | 'title'> = {
    countryId,
    help: regionFieldProps.help ?? t('Tooltip.Region'),
    title: regionFieldProps.title ?? t('Selector.Region')
  };
  const commonRegionProvinceFieldProps: Pick<
    RegionFieldProps<T>,
    'countryId' | 'help' | 'title' | 'skipQuery' | 'provinceId'
  > = {
    countryId,
    help: provinceFieldProps.help ?? t('Tooltip.City'),
    title: provinceFieldProps.title ?? t('Selector.City'),
    skipQuery: isMultipleProvinces ? provinceValue.length !== 1 : !provinceValue,
    provinceId: isMultipleProvinces ? (provinceValue.length === 1 ? provinceValue.at(0) : '') : provinceValue
  };

  /* 
    for non ID influencer, we stay same to show Region. Region from API = Region on UI.
    for ID influencer, we need to differently show them UI.                
    Province from API = Region on UI.
    Region from API = City on UI.
  */
  return isIndonesiaCountry(countryId) ? (
    <>
      {provinceFieldProps.multiple ? (
        <ProvinceField<T>
          onChange={(name, value) => {
            setValue<string>(name, value, { shouldValidate: true });

            if (!isEqual(value, provinceValue)) {
              setValue<string>(regionFieldProps.name, regionFieldProps.multiple ? [] : '', { shouldValidate: true });
            }
          }}
          {...(provinceFieldProps as MultipleProvinceFieldProps<T>)}
          {...commonProvinceFieldProps}
          multiple={true}
        />
      ) : (
        <ProvinceField<T>
          onChange={(name, value) => {
            setValue<string>(name, value, { shouldValidate: true });

            if (value !== provinceValue) {
              setValue<string>(regionFieldProps.name, regionFieldProps.multiple ? [] : '', { shouldValidate: true });
            }
          }}
          {...(provinceFieldProps as SingleProvinceFieldProps<T>)}
          {...commonProvinceFieldProps}
          multiple={false}
        />
      )}

      {regionFieldProps.multiple ? (
        <RegionField<T>
          {...(regionFieldProps as MultipleRegionFieldProps<T>)}
          {...commonRegionProvinceFieldProps}
          multiple={true}
        />
      ) : (
        <RegionField<T>
          {...(regionFieldProps as SingleRegionFieldProps<T>)}
          {...commonRegionProvinceFieldProps}
          multiple={false}
        />
      )}
    </>
  ) : regionFieldProps.multiple ? (
    <RegionField<T> {...(regionFieldProps as MultipleRegionFieldProps<T>)} countryId={countryId} multiple={true} />
  ) : (
    <RegionField<T> {...(regionFieldProps as SingleRegionFieldProps<T>)} countryId={countryId} multiple={false} />
  );
};
